/**
 * wslData.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Data layer for the WSL component.
 *
 * Priority:
 *   1. Fetch live JSON from the Cloudflare Worker proxy (fresh every 30 min)
 *   2. Fall back to the hardcoded GW19 snapshot below if the fetch fails
 *
 * After deploying the worker, set PROXY_URL to your worker's URL.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Set this after running `wrangler deploy` ─────────────────────────────────
// It will look like: https://wsl-proxy.YOUR-NAME.workers.dev/wsl
const PROXY_URL = 'https://wsl-proxy.footballgirl.workers.dev/wsl';

// ─── Season meta ─────────────────────────────────────────────────────────────
export const WSL_SEASON    = '2025/26';
export const WSL_CURRENT_GW = 19;
export const WSL_TOTAL_GW   = 22;
export const WSL_SCRAPED    = '2026-04-21'; // date of the hardcoded snapshot

// ─── Hardcoded fallback — GW19 snapshot (wslfootball.com + ESPN) ─────────────
// Used automatically if the proxy is down or PROXY_URL is not yet set.
export const WSL_STANDINGS_FALLBACK = [
    { pos:1,  short:'MCI', name:'Manchester City',        color:'#6CABDD', p:19, w:16, d:1, l:2,  gf:55, ga:15, pts:49, form:['W','W','D','W','L'], zone:'cl' },
    { pos:2,  short:'CHE', name:'Chelsea',                color:'#034694', p:19, w:12, d:4, l:3,  gf:36, ga:18, pts:40, form:['W','D','W','W','W'], zone:'cl' },
    { pos:3,  short:'ARS', name:'Arsenal',                color:'#EF0107', p:17, w:11, d:5, l:1,  gf:38, ga:12, pts:38, form:['W','W','W','W','D'], zone:'cl' },
    { pos:4,  short:'MNU', name:'Manchester United',      color:'#DA291C', p:19, w:11, d:5, l:3,  gf:37, ga:20, pts:38, form:['L','W','D','W','W'], zone:''   },
    { pos:5,  short:'TOT', name:'Tottenham Hotspur',      color:'#132257', p:19, w:9,  d:2, l:8,  gf:31, ga:36, pts:29, form:['L','L','L','W','W'], zone:''   },
    { pos:6,  short:'BHA', name:'Brighton & Hove Albion', color:'#0057B8', p:18, w:6,  d:3, l:9,  gf:21, ga:22, pts:21, form:['W','D','L','L','W'], zone:''   },
    { pos:7,  short:'LCL', name:'London City Lionesses',  color:'#FF6600', p:19, w:6,  d:3, l:10, gf:20, ga:31, pts:21, form:['D','D','L','L','W'], zone:''   },
    { pos:8,  short:'EVE', name:'Everton',                color:'#003399', p:19, w:6,  d:2, l:11, gf:23, ga:32, pts:20, form:['L','L','W','W','W'], zone:''   },
    { pos:9,  short:'AST', name:'Aston Villa',            color:'#95BFE5', p:19, w:5,  d:5, l:9,  gf:27, ga:41, pts:20, form:['L','W','D','L','L'], zone:''   },
    { pos:10, short:'LFC', name:'Liverpool',              color:'#C8102E', p:19, w:4,  d:5, l:10, gf:20, ga:29, pts:17, form:['W','D','W','L','L'], zone:''   },
    { pos:11, short:'WHU', name:'West Ham United',        color:'#7A263A', p:19, w:3,  d:4, l:12, gf:16, ga:41, pts:13, form:['D','L','D','L','L'], zone:''   },
    { pos:12, short:'LEI', name:'Leicester City',         color:'#003090', p:18, w:2,  d:3, l:13, gf:9,  ga:36, pts:9,  form:['L','L','L','L','L'], zone:'po' },
];

export const WSL_TOP_SCORERS_FALLBACK = [
    { rank:1,  name:'Khadija Shaw',      club:'Manchester City',   goals:15, apps:16, initials:'KS' },
    { rank:2,  name:'Kirsty Hanson',     club:'Aston Villa',       goals:9,  apps:16, initials:'KH' },
    { rank:3,  name:'Vivianne Miedema',  club:'Manchester City',   goals:8,  apps:16, initials:'VM' },
    { rank:4,  name:'Kerolin',           club:'Manchester City',   goals:7,  apps:9,  initials:'KR' },
    { rank:5,  name:'Jess Park',         club:'Manchester United', goals:6,  apps:16, initials:'JP' },
    { rank:5,  name:'Alyssa Thompson',   club:'Chelsea',           goals:6,  apps:14, initials:'AT' },
    { rank:5,  name:'Cathinka Tandberg', club:'Tottenham Hotspur', goals:6,  apps:13, initials:'CT' },
    { rank:5,  name:'Kiko Seike',        club:'Brighton',          goals:6,  apps:15, initials:'KS' },
    { rank:10, name:'Bethany England',   club:'Tottenham Hotspur', goals:5,  apps:16, initials:'BE' },
    { rank:10, name:'Alessia Russo',     club:'Arsenal',           goals:5,  apps:14, initials:'AR' },
];

export const WSL_FIXTURES_FALLBACK = [
    { date:'2026-03-28', home:'Arsenal',          homeColor:'#EF0107', away:'Tottenham Hotspur', awayColor:'#132257', homeGoals:5, awayGoals:2, status:'FT' },
    { date:'2026-03-28', home:'Manchester United', homeColor:'#DA291C', away:'Manchester City',   awayColor:'#6CABDD', homeGoals:0, awayGoals:3, status:'FT' },
    { date:'2026-03-28', home:'Everton',           homeColor:'#003399', away:'Liverpool',         awayColor:'#C8102E', homeGoals:2, awayGoals:3, status:'FT' },
    { date:'2026-04-05', home:'Chelsea',           homeColor:'#034694', away:'Arsenal',           awayColor:'#EF0107', homeGoals:1, awayGoals:1, status:'FT' },
    { date:'2026-04-05', home:'Manchester City',   homeColor:'#6CABDD', away:'Aston Villa',       awayColor:'#95BFE5', homeGoals:4, awayGoals:0, status:'FT' },
    { date:'2026-04-26', home:'Arsenal',           homeColor:'#EF0107', away:'Manchester City',   awayColor:'#6CABDD', homeGoals:null, awayGoals:null, status:'NS' },
    { date:'2026-04-26', home:'Chelsea',           homeColor:'#034694', away:'Tottenham Hotspur', awayColor:'#132257', homeGoals:null, awayGoals:null, status:'NS' },
    { date:'2026-04-27', home:'Liverpool',         homeColor:'#C8102E', away:'Everton',           awayColor:'#003399', homeGoals:null, awayGoals:null, status:'NS' },
    { date:'2026-05-03', home:'Manchester City',   homeColor:'#6CABDD', away:'Chelsea',           awayColor:'#034694', homeGoals:null, awayGoals:null, status:'NS' },
    { date:'2026-05-10', home:'Aston Villa',       homeColor:'#95BFE5', away:'Arsenal',           awayColor:'#EF0107', homeGoals:null, awayGoals:null, status:'NS' },
];

// ─── Derived helpers ──────────────────────────────────────────────────────────
export function wslPts(t)  { return t.pts ?? (t.w * 3 + t.d); }
export function wslGD(t)   { return t.gf - t.ga; }

// ─── Live fetch ───────────────────────────────────────────────────────────────
/**
 * Tries to fetch fresh data from the Cloudflare Worker proxy.
 * Returns the parsed JSON on success, null on any failure.
 * The component falls back to the hardcoded snapshot automatically.
 *
 * @returns {Promise<{standings, topScorers, fixtures, meta}|null>}
 */
export async function fetchLiveWSLData() {
    // If PROXY_URL still has the placeholder, skip the fetch
    if (!PROXY_URL || PROXY_URL.includes('footballgirl')) {
        console.info('[WSL] PROXY_URL not set — using fallback data.');
        return null;
    }

    try {
        const controller = new AbortController();
        const timeout    = setTimeout(() => controller.abort(), 5000); // 5s timeout
        const res = await fetch(PROXY_URL, {
            signal: controller.signal,
            headers: { Accept: 'application/json' },
        });
        clearTimeout(timeout);

        if (!res.ok) throw new Error(`Proxy returned ${res.status}`);

        const json = await res.json();

        // Validate the response shape before trusting it
        if (!Array.isArray(json.standings) || json.standings.length === 0) {
            throw new Error('Proxy returned empty standings');
        }

        console.info(`[WSL] Live data loaded (scraped: ${json.meta?.scraped ?? 'unknown'})`);
        return {
            standings:  json.standings,
            topScorers: json.topScorers ?? WSL_TOP_SCORERS_FALLBACK,
            fixtures:   json.fixtures   ?? WSL_FIXTURES_FALLBACK,
            meta:       json.meta,
        };
    } catch (err) {
        console.warn('[WSL] Live fetch failed, using fallback snapshot:', err.message);
        return null;
    }
}

// Ensure these are at the absolute bottom, outside all brackets
export const WSL_STANDINGS = WSL_STANDINGS_FALLBACK;
export const WSL_TOP_SCORERS = WSL_TOP_SCORERS_FALLBACK;
export const WSL_FIXTURES = WSL_FIXTURES_FALLBACK;